import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Modal, Spinner, Badge } from 'react-bootstrap';
import { BsX, BsFiletypeCsv, BsFiletypePdf, BsEnvelope } from 'react-icons/bs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import { baseURL } from '../../helpers/baseURL';
import { headers } from '../../helpers/Requests';
import { formatDateLine } from '../../helpers/formatDate';
import { ucFirst } from '../../helpers/generic';
import successAlert from '../../helpers/successAlert';
import failAlert from '../../helpers/failAlert';
import DatePicker from '../../helpers/DatePicker';
import { fetchStatuses, fetchDepartments } from '../../redux/actions';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 25;
const MAX_TICKETS = 5000; // keep in sync with TicketsSummaryController::MAX_TICKETS
const MAX_RANGE_MONTHS = 6; // keep in sync with TicketsSummaryController::MAX_RANGE_MONTHS

// ── Date preset helpers (modal-local — intentionally NOT shared with the
// Dashboard's date dropdown, whose "This Week" means Mon→Sun).
//
// For the on-demand summary email, "This Week" means Monday → today, which
// is what most users intuitively expect from a weekly status report.

const padTwo = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${padTwo(d.getMonth() + 1)}-${padTwo(d.getDate())}`;

// IMPORTANT: parse `YYYY-MM-DD` as a LOCAL midnight, never UTC.
// `new Date('2025-11-15')` parses as UTC and produces DST off-by-one bugs
// when combined with the local-time `setMonth`/`setDate` mutators below.
const parseLocalDate = (s) => {
	if (!s) return null;
	const parts = String(s).split('-').map(Number);
	if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
	const [y, m, d] = parts;
	return new Date(y, m - 1, d);
};

const startOfIsoWeek = (ref = new Date()) => {
	const d = new Date(ref);
	const dow = d.getDay(); // 0=Sunday … 6=Saturday
	d.setDate(d.getDate() - dow + (dow === 0 ? -6 : 1));
	d.setHours(0, 0, 0, 0);
	return d;
};

const addMonths = (d, n) => {
	const r = new Date(d);
	r.setMonth(r.getMonth() + n);
	return r;
};

const minDate = (a, b) => (a.getTime() <= b.getTime() ? a : b);

export const PRESETS = [
	{ key: 'this_week',     label: 'This Week' },     // Mon → min(today, Fri)
	{ key: 'last_week',     label: 'Last Week' },     // previous Mon → previous Fri
	{ key: 'this_month',    label: 'This Month' },    // 1st of month → today
	{ key: 'last_month',    label: 'Last Month' },    // full previous month
	{ key: 'last_3_months', label: 'Last 3 Months' }, // today − 3 months → today
	{ key: 'last_6_months', label: 'Last 6 Months' }, // today − 6 months → today  (= max range)
];

export const getModalPresetRange = (key) => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	if (key === 'this_week') {
		const monday = startOfIsoWeek(today);
		const friday = new Date(monday);
		friday.setDate(monday.getDate() + 4);
		return [isoDate(monday), isoDate(minDate(today, friday))];
	}
	if (key === 'last_week') {
		const monday = startOfIsoWeek(today);
		monday.setDate(monday.getDate() - 7); // previous Monday
		const friday = new Date(monday);
		friday.setDate(monday.getDate() + 4); // previous Friday
		return [isoDate(monday), isoDate(friday)];
	}
	if (key === 'this_month') {
		const start = new Date(today.getFullYear(), today.getMonth(), 1);
		return [isoDate(start), isoDate(today)];
	}
	if (key === 'last_month') {
		const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
		const end   = new Date(today.getFullYear(), today.getMonth(),     0);
		return [isoDate(start), isoDate(end)];
	}
	if (key === 'last_3_months') {
		return [isoDate(addMonths(today, -3)), isoDate(today)];
	}
	if (key === 'last_6_months') {
		return [isoDate(addMonths(today, -6)), isoDate(today)];
	}
	return [null, null];
};

const rangeExceedsLimit = (fromStr, toStr) => {
	const from = parseLocalDate(fromStr);
	const to   = parseLocalDate(toStr);
	if (!from || !to) return false;
	const maxTo = addMonths(from, MAX_RANGE_MONTHS);
	return to > maxTo;
};

// Returns a [from, to] pair that is guaranteed to satisfy the 6-month rule
// (and from ≤ to). Invalid pre-filled ranges from the Dashboard / Tickets
// page get silently corrected so the modal never opens in a red state.
const clampDateRange = (fromStr, toStr) => {
	const fromLocal = parseLocalDate(fromStr);
	const toLocal   = parseLocalDate(toStr);
	if (!fromLocal || !toLocal) return [fromStr, toStr];
	if (toLocal < fromLocal) return [fromStr, fromStr];
	const maxTo = addMonths(fromLocal, MAX_RANGE_MONTHS);
	if (toLocal > maxTo) return [fromStr, isoDate(maxTo)];
	return [fromStr, toStr];
};

/**
 * On-demand "Email this summary" modal — used from the Tickets list and the
 * Dashboard filter bar. Generates CSV/PDF on the client (same code path as
 * TicketsExport) and POSTs to the backend, which re-runs the filtered query,
 * builds the HTML body and attaches the uploaded files.
 *
 * @param {Object}   props
 * @param {Function} props.closeModal           - called to close the modal.
 * @param {number[]} [props.initialDepartments] - department ids to pre-select.
 * @param {number[]} [props.initialStatuses]    - status ids to pre-select.
 * @param {string}   [props.initialDateFrom]    - YYYY-MM-DD.
 * @param {string}   [props.initialDateTo]      - YYYY-MM-DD.
 */
const SummaryEmailModal = ({
	closeModal,
	initialDepartments = [],
	initialStatuses = [],
	initialDateFrom = null,
	initialDateTo = null,
}) => {
	const dispatch       = useDispatch();
	const allDepartments = useSelector(state => state?.departmentsReducer?.departments ?? []);
	const allStatuses    = useSelector(state => state?.statusesReducer?.statuses ?? []);
	const departmentsLoading = useSelector(state => state?.departmentsReducer?.loading);
	const statusesLoading    = useSelector(state => state?.statusesReducer?.loading);

	// Self-heal: if a user opens this modal before the page has finished
	// hydrating these reducers, fetch them so the checkbox lists populate.
	useEffect(() => {
		if (allDepartments.length === 0 && !departmentsLoading) {
			dispatch(fetchDepartments());
		}
		if (allStatuses.length === 0 && !statusesLoading) {
			dispatch(fetchStatuses());
		}
	}, []);

	const [departments, setDepartments] = useState(initialDepartments);
	const [statuses, setStatuses]       = useState(initialStatuses);

	// Compute the starting range once, clamping any pre-filled invalid pair so
	// the modal never opens already-violating the 6-month rule.
	const [initialFrom, initialTo] = useMemo(() => {
		const defaults = getModalPresetRange('this_week');
		const rawFrom = initialDateFrom ?? defaults[0];
		const rawTo   = initialDateTo   ?? defaults[1];
		return clampDateRange(rawFrom, rawTo);
	}, []);

	const [dateFrom, setDateFrom] = useState(initialFrom);
	const [dateTo, setDateTo]     = useState(initialTo);
	const [activePreset, setActivePreset] = useState(initialDateFrom ? null : 'this_week');

	const [format, setFormat] = useState('both');
	const [emails, setEmails] = useState([]);
	const [emailInput, setEmailInput] = useState('');
	const [note, setNote] = useState('');
	const [error, setError] = useState('');
	const [isSending, setIsSending] = useState(false);
	const [oversizeInfo, setOversizeInfo] = useState(null); // { count, limit }

	const emailInputRef = useRef(null);

	useEffect(() => {
		// keep statuses pre-selected only if they actually exist in the loaded
		// reducer state (avoids stale ids from URL params).
		if (initialStatuses.length > 0 && allStatuses.length > 0) {
			const validIds = new Set(allStatuses.map(s => s.id));
			setStatuses(initialStatuses.filter(id => validIds.has(id)));
		}
	}, [allStatuses.length]);

	const clearWarnings = () => {
		setOversizeInfo(null);
	};

	const toggle = (collection, setter, id) => {
		setter(collection.includes(id) ? collection.filter(x => x !== id) : [...collection, id]);
		clearWarnings();
	};

	const applyPreset = (key) => {
		const [from, to] = getModalPresetRange(key);
		setDateFrom(from);
		setDateTo(to);
		setActivePreset(key);
		clearWarnings();
	};

	const onCustomFromChange = (d) => {
		const newFromIso = d ? formatDateLine(d) : null;
		setDateFrom(newFromIso);
		setActivePreset(null);
		clearWarnings();
		// If the existing "To" is now > 6 months after the new "From" (rare,
		// but possible if state somehow drifted), clamp it down in the same
		// render — no red-error flicker.
		if (newFromIso && dateTo) {
			const [, clampedTo] = clampDateRange(newFromIso, dateTo);
			if (clampedTo !== dateTo) setDateTo(clampedTo);
		}
	};
	const onCustomToChange = (d) => {
		setDateTo(d ? formatDateLine(d) : null);
		setActivePreset(null);
		clearWarnings();
	};

	const tryAddEmail = (raw) => {
		const value = (raw ?? '').trim().replace(/[,;]+$/, '');
		if (!value) return false;
		if (!EMAIL_REGEX.test(value)) {
			setError(`"${value}" is not a valid email.`);
			return false;
		}
		if (emails.includes(value.toLowerCase())) {
			setError(`${value} is already in the list.`);
			return false;
		}
		if (emails.length >= MAX_RECIPIENTS) {
			setError(`You can add up to ${MAX_RECIPIENTS} recipients.`);
			return false;
		}
		setEmails([...emails, value.toLowerCase()]);
		setError('');
		return true;
	};

	const handleEmailKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
			if (emailInput.trim() !== '') {
				e.preventDefault();
				if (tryAddEmail(emailInput)) setEmailInput('');
			}
		} else if (e.key === 'Backspace' && emailInput === '' && emails.length > 0) {
			setEmails(emails.slice(0, -1));
		}
	};

	const handleEmailBlur = () => {
		if (emailInput.trim() !== '') {
			if (tryAddEmail(emailInput)) setEmailInput('');
		}
	};

	const handleEmailPaste = (e) => {
		const text = e.clipboardData.getData('text');
		if (text && /[,;\s]/.test(text)) {
			e.preventDefault();
			text.split(/[,;\s]+/).forEach(t => tryAddEmail(t));
			setEmailInput('');
		}
	};

	const removeEmail = (value) => {
		setEmails(emails.filter(e => e !== value));
	};

	// ── Attachment generation (mirrors TicketsExport.js) ─────────────────────
	const creatorName = (ticket) => {
		const value = ticket.created_by;
		if (typeof value === 'string') return value;
		if (value?.name) return value.name;
		return ticket.created_by_user?.name ?? ticket.creator?.name ?? '';
	};

	const assigneeNames = (ticket) => {
		const names = ticket.assignees?.map(a => a.name).filter(Boolean) ?? [];
		if (names.length) return names.join(', ');
		return ticket.user?.name ?? '';
	};

	const buildExportRows = (tickets) => {
		const head = ['ID', 'Title', 'Department', 'Status', 'Assignee', 'Created By', 'Created At', 'Priority', 'Type'];
		const body = tickets.map(t => [
			t.ticket_id ?? t.id ?? '',
			t.title ?? '',
			t.department?.name ?? '',
			t.status?.name ? ucFirst(t.status.name) : '',
			assigneeNames(t),
			creatorName(t),
			t.created_at ?? '',
			t.priority?.name ?? '',
			t.type?.name ?? '',
		]);
		return { head, body };
	};

	const buildCsvBlob = (rows) => {
		const escape = (cell) => {
			const s = (cell ?? '').toString();
			return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
		};
		const csv = [rows.head.map(escape).join(',')]
			.concat(rows.body.map(r => r.map(escape).join(',')))
			.join('\n');
		return new Blob([csv], { type: 'text/csv' });
	};

	const buildPdfBlob = (rows, periodLabel) => {
		const doc = new jsPDF();
		doc.setFontSize(14);
		doc.text('Tickets Summary', 14, 16);
		if (periodLabel) {
			doc.setFontSize(10);
			doc.text(periodLabel, 14, 22);
		}
		doc.autoTable({
			head: [rows.head],
			body: rows.body,
			startY: 26,
			styles: { fontSize: 8 },
			headStyles: { fillColor: [37, 99, 235] },
		});
		return doc.output('blob');
	};

	const fetchFilteredTickets = async () => {
		const params = new URLSearchParams();
		if (departments.length) params.set('department', departments.join(','));
		if (statuses.length) {
			// /api/tickets accepts status NAMES (legacy) — translate ids → names.
			const names = allStatuses
				.filter(s => statuses.includes(s.id))
				.map(s => s.name);
			if (names.length) params.set('status', names.join(','));
		}
		if (dateFrom && dateTo) {
			params.set('created_at', 'range');
			params.set('created_at_range', `${dateFrom},${dateTo}`);
		}
		params.set('export', 'true');

		const response = await axios.get(`${baseURL}tickets?${params.toString()}`, headers);
		return Array.isArray(response.data) ? response.data : (response.data?.tickets ?? []);
	};

	const validateForm = () => {
		if (statuses.length === 0) return 'Please select at least one status.';
		if (!dateFrom || !dateTo) return 'Please select a date range.';
		const fromLocal = parseLocalDate(dateFrom);
		const toLocal   = parseLocalDate(dateTo);
		if (fromLocal && toLocal && fromLocal > toLocal) return 'The "From" date must be before the "To" date.';
		if (rangeExceedsLimit(dateFrom, dateTo)) return `Date range cannot exceed ${MAX_RANGE_MONTHS} months.`;
		if (emails.length === 0) return 'Please add at least one recipient email.';
		return '';
	};

	const handleSubmit = async (e) => {
		if (e && e.preventDefault) e.preventDefault();
		const formError = validateForm();
		if (formError) {
			setError(formError);
			return;
		}

		setError('');
		clearWarnings();
		setIsSending(true);

		try {
			const tickets = await fetchFilteredTickets();

			// Client-side size guard — avoid freezing the browser building a
			// PDF for tens of thousands of tickets, and avoid uploading a huge
			// attachment that the server will reject anyway.
			if (tickets.length > MAX_TICKETS) {
				setOversizeInfo({ count: tickets.length, limit: MAX_TICKETS });
				return;
			}

			if (tickets.length === 0) {
				const msg =
					'No tickets match the selected filters. Narrow or change the departments, statuses, or date range and try again.';
				setError(msg);
				failAlert(msg);
				return;
			}

			const rows = buildExportRows(tickets);

			const formData = new FormData();
			departments.forEach(id => formData.append('department_ids[]', id));
			statuses.forEach(id => formData.append('status_ids[]', id));
			formData.append('date_from', dateFrom);
			formData.append('date_to', dateTo);
			formData.append('format', format);
			if (note.trim()) formData.append('note', note.trim());
			emails.forEach(em => formData.append('emails[]', em));

			const fileTag = `tickets-summary-${dateFrom}_to_${dateTo}`;
			const periodLabel = `Period: ${dateFrom} to ${dateTo}`;
			if (format === 'csv' || format === 'both') {
				formData.append('attachments[]', buildCsvBlob(rows), `${fileTag}.csv`);
			}
			if (format === 'pdf' || format === 'both') {
				formData.append('attachments[]', buildPdfBlob(rows, periodLabel), `${fileTag}.pdf`);
			}

			await axios.post(`${baseURL}tickets-summary/email`, formData, {
				headers: {
					// Authorization only — let axios pick the correct
					// multipart/form-data boundary for us.
					Authorization: headers.headers.Authorization,
				},
			});

			successAlert('Summary email sent');
			closeModal();
		} catch (err) {
			const data = err?.response?.data;
			if (data?.code === 'NO_TICKETS_MATCHED') {
				const msg = data?.message
					|| 'No tickets match the selected filters. Narrow or change the departments, statuses, or date range and try again.';
				setError(msg);
				failAlert(msg);
				return;
			}
			if (data?.code === 'TOO_MANY_TICKETS') {
				setOversizeInfo({ count: data.count ?? null, limit: data.limit ?? MAX_TICKETS });
				setError('');
				return;
			}
			const message = data?.message
				|| (data?.errors
					? Object.values(data.errors).flat().join(' ')
					: null)
				|| 'Failed to send the summary email.';
			setError(message);
			failAlert(message);
		} finally {
			setIsSending(false);
		}
	};

	const totalDepartments = allDepartments.length;
	const totalStatuses    = allStatuses.length;
	const statusLabel = `${statuses.length} of ${totalStatuses} selected`;

	const selectAllDepartments = () => setDepartments(allDepartments.map(d => d.id));
	const clearDepartments = () => setDepartments([]);
	const selectAllStatuses = () => setStatuses(allStatuses.map(s => s.id));
	const clearStatuses = () => setStatuses([]);

	const filtersPeriodSummary = useMemo(() => {
		if (!dateFrom || !dateTo) return null;
		return `${dateFrom} → ${dateTo}`;
	}, [dateFrom, dateTo]);

	// Disable dates that would violate the 6-month rule or the From≤To rule.
	// Recompute whenever either bound changes so the calendars stay in sync.
	const { fromPickerMinDate, fromPickerMaxDate, toPickerMinDate, toPickerMaxDate } = useMemo(() => {
		const fromLocal = parseLocalDate(dateFrom);
		const toLocal   = parseLocalDate(dateTo);
		return {
			// "From" can't be more than 6 months before the currently-set "To"
			fromPickerMinDate: toLocal ? addMonths(toLocal, -MAX_RANGE_MONTHS) : undefined,
			// "From" can't be after the currently-set "To"
			fromPickerMaxDate: toLocal || undefined,
			// "To" must be on or after "From"
			toPickerMinDate:   fromLocal || undefined,
			// "To" can't be more than 6 months after "From"
			toPickerMaxDate:   fromLocal ? addMonths(fromLocal, MAX_RANGE_MONTHS) : undefined,
		};
	}, [dateFrom, dateTo]);

	return (
		<Modal
			show={true}
			onHide={isSending ? () => {} : closeModal}
			size="lg"
			centered
			backdrop={isSending ? 'static' : true}
			keyboard={!isSending}
		>
			<Form onSubmit={handleSubmit}>
				<Modal.Header closeButton={!isSending}>
					<Modal.Title style={{ fontSize: '18px' }}>
						<BsEnvelope className="me-2 flex-shrink-0" size={18} aria-hidden />Email Tickets Summary
					</Modal.Title>
				</Modal.Header>

				<Modal.Body style={{ fontSize: '14px' }}>
					<div className="text-muted mb-3" style={{ fontSize: '13px' }}>
						Pick the filters below — a summary of the matching tickets will be emailed
						to you and CC'd to the recipients you choose.
					</div>

					{/* Departments */}
					<Form.Group className="mb-3">
						<div className="d-flex align-items-center justify-content-between mb-1">
							<Form.Label className="mb-0 fw-bold">
								Departments
								{departments.length > 0 ? (
									<span className="text-muted fw-normal">
										{' '}({departments.length} selected)
									</span>
								) : null}
							</Form.Label>
							<div>
								<Button size="sm" variant="link" className="p-0 me-2" onClick={selectAllDepartments} type="button">Select all</Button>
								<Button size="sm" variant="link" className="p-0 text-danger" onClick={clearDepartments} type="button">Clear</Button>
							</div>
						</div>
						<div className="border rounded p-2" style={{ maxHeight: '140px', overflowY: 'auto' }}>
							{totalDepartments === 0 ? (
								<div className="text-muted small">No departments available.</div>
							) : (
								allDepartments.map(dep => (
									<Form.Check
										key={dep.id}
										type="checkbox"
										id={`summary-dept-${dep.id}`}
										label={(
											<span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
												<span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: dep.color || '#6b7280' }} />
												{dep.name}
											</span>
										)}
										checked={departments.includes(dep.id)}
										onChange={() => toggle(departments, setDepartments, dep.id)}
									/>
								))
							)}
						</div>
						<div className="text-muted mt-1" style={{ fontSize: '12px' }}>
							Leave empty to include all departments you have access to.
						</div>
					</Form.Group>

					{/* Statuses */}
					<Form.Group className="mb-3">
						<div className="d-flex align-items-center justify-content-between mb-1">
							<Form.Label className="mb-0 fw-bold">Statuses <span className="text-muted fw-normal">({statusLabel})</span></Form.Label>
							<div>
								<Button size="sm" variant="link" className="p-0 me-2" onClick={selectAllStatuses} type="button">Select all</Button>
								<Button size="sm" variant="link" className="p-0 text-danger" onClick={clearStatuses} type="button">Clear</Button>
							</div>
						</div>
						<div className="border rounded p-2" style={{ maxHeight: '140px', overflowY: 'auto' }}>
							{totalStatuses === 0 ? (
								<div className="text-muted small">No statuses available.</div>
							) : (
								allStatuses.map(st => (
									<Form.Check
										key={st.id}
										type="checkbox"
										id={`summary-status-${st.id}`}
										label={(
											<span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
												<span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: st.color || '#6b7280' }} />
												{ucFirst(st.name)}
											</span>
										)}
										checked={statuses.includes(st.id)}
										onChange={() => toggle(statuses, setStatuses, st.id)}
									/>
								))
							)}
						</div>
					</Form.Group>

					{/* Date range */}
					<Form.Group className="mb-3">
						<Form.Label className="fw-bold mb-1">
							Date range
							{filtersPeriodSummary && <span className="text-muted fw-normal ms-2" style={{ fontSize: '12px' }}>{filtersPeriodSummary}</span>}
						</Form.Label>
						<div className="d-flex flex-wrap gap-1 mb-2">
							{PRESETS.map(p => (
								<Button
									key={p.key}
									type="button"
									size="sm"
									variant={activePreset === p.key ? 'primary' : 'light'}
									onClick={() => applyPreset(p.key)}
								>
									{p.label}
								</Button>
							))}
						</div>
						<div className="text-muted mb-2" style={{ fontSize: '12px' }}>
							Maximum range: {MAX_RANGE_MONTHS} months.
						</div>
						<div className="d-flex gap-2 flex-wrap">
							<div style={{ minWidth: '160px', flex: 1 }}>
								<div className="text-muted small mb-1">From</div>
								<DatePicker
									handleDateChange={onCustomFromChange}
									selected={parseLocalDate(dateFrom)}
									minDate={fromPickerMinDate}
									maxDate={fromPickerMaxDate}
								/>
							</div>
							<div style={{ minWidth: '160px', flex: 1 }}>
								<div className="text-muted small mb-1">To</div>
								<DatePicker
									handleDateChange={onCustomToChange}
									selected={parseLocalDate(dateTo)}
									minDate={toPickerMinDate}
									maxDate={toPickerMaxDate}
								/>
							</div>
						</div>
						{rangeExceedsLimit(dateFrom, dateTo) && (
							<div className="text-danger mt-2" style={{ fontSize: '12px' }}>
								Date range cannot exceed {MAX_RANGE_MONTHS} months — please narrow it.
							</div>
						)}
					</Form.Group>

					{/* Attachment format */}
					<Form.Group className="mb-3">
						<Form.Label className="fw-bold">Attachment format</Form.Label>
						<div className="d-flex gap-3 flex-wrap">
							<Form.Check
								type="radio"
								id="summary-format-csv"
								name="summary-format"
								label={<span><BsFiletypeCsv className="me-1" />CSV</span>}
								checked={format === 'csv'}
								onChange={() => setFormat('csv')}
							/>
							<Form.Check
								type="radio"
								id="summary-format-pdf"
								name="summary-format"
								label={<span><BsFiletypePdf className="me-1" />PDF</span>}
								checked={format === 'pdf'}
								onChange={() => setFormat('pdf')}
							/>
							<Form.Check
								type="radio"
								id="summary-format-both"
								name="summary-format"
								label="Both"
								checked={format === 'both'}
								onChange={() => setFormat('both')}
							/>
						</div>
					</Form.Group>

					{/* Recipient emails */}
					<Form.Group className="mb-3">
						<Form.Label className="fw-bold">
							Recipient emails (CC) <span className="text-muted fw-normal" style={{ fontSize: '12px' }}>— press Enter or comma to add</span>
						</Form.Label>
						<div
							className="form-control d-flex flex-wrap align-items-center"
							style={{ minHeight: '38px', gap: '4px', cursor: 'text' }}
							onClick={() => emailInputRef.current?.focus()}
						>
							{emails.map(em => (
								<Badge
									key={em}
									bg="primary"
									className="d-inline-flex align-items-center"
									style={{ fontSize: '12px', padding: '4px 6px' }}
								>
									{em}
									<BsX
										style={{ cursor: 'pointer', marginLeft: '2px', fontSize: '16px' }}
										onClick={(e) => { e.stopPropagation(); removeEmail(em); }}
									/>
								</Badge>
							))}
							<input
								ref={emailInputRef}
								type="text"
								value={emailInput}
								onChange={(e) => { setEmailInput(e.target.value); setError(''); }}
								onKeyDown={handleEmailKeyDown}
								onBlur={handleEmailBlur}
								onPaste={handleEmailPaste}
								placeholder={emails.length === 0 ? 'name@example.com' : ''}
								style={{ flex: 1, minWidth: '160px', border: 'none', outline: 'none', fontSize: '14px', background: 'transparent' }}
							/>
						</div>
						<div className="text-muted mt-1" style={{ fontSize: '12px' }}>
							The summary will be sent <strong>to you</strong>, with these addresses in CC. Up to {MAX_RECIPIENTS} recipients.
						</div>
					</Form.Group>

					{/* Note */}
					<Form.Group className="mb-2">
						<Form.Label className="fw-bold">Message <span className="text-muted fw-normal" style={{ fontSize: '12px' }}>(optional)</span></Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Add a short message for the recipients..."
							maxLength={2000}
						/>
					</Form.Group>

					{oversizeInfo && (
						<div className="alert alert-danger py-2 px-3 mb-0" style={{ fontSize: '13px' }}>
							<strong>
								Too many tickets to email
								{oversizeInfo.count != null ? ` (${oversizeInfo.count})` : ''}.
							</strong>
							<div className="mt-1">
								The summary email is capped at <strong>{oversizeInfo.limit}</strong> tickets to keep the
								attachments and email size reasonable. Please narrow the departments, statuses or
								date range and try again.
							</div>
						</div>
					)}

					{error && (
						<div className="alert alert-danger py-2 px-3 mb-0" style={{ fontSize: '13px' }}>
							{error}
						</div>
					)}
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" onClick={closeModal} disabled={isSending} type="button">
						Cancel
					</Button>
					<Button variant="primary" type="submit" disabled={isSending}>
						{isSending ? (
							<>
								<Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
								Sending...
							</>
						) : (
							<><BsEnvelope className="me-1 flex-shrink-0" size={18} aria-hidden />Send Summary</>
						)}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};

export default SummaryEmailModal;
